import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, MapPin, Plus, Trash2, Gift, History, Edit, Save, X, Copy, Check, Share2, Crown, Star, Award, LogOut } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

const CustomerProfile = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({ name: '', email: '' });
  
  // Loyalty state
  const [loyaltyBalance, setLoyaltyBalance] = useState(null);
  const [loyaltyHistory, setLoyaltyHistory] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [loyaltyConfig, setLoyaltyConfig] = useState(null);
  
  // Address state
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({ label: 'Home', address: '', is_default: false });
  
  // Referral state
  const [showReferral, setShowReferral] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [applyingReferral, setApplyingReferral] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('customerToken');
    if (!token) {
      navigate('/');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [profileRes, balanceRes, historyRes, referralsRes, loyaltySettingsRes] = await Promise.all([
        api.get('/customers/me'),
        api.get('/loyalty/balance'),
        api.get('/loyalty/history'),
        api.get('/loyalty/referrals').catch(() => ({ data: null })),
        api.get('/loyalty/settings').catch(() => ({ data: { loyalty_enabled: true } }))
      ]);
      
      setCustomer(profileRes.data);
      setEditData({ 
        name: profileRes.data.name || '', 
        email: profileRes.data.email || '' 
      });
      setLoyaltyBalance(balanceRes.data);
      setLoyaltyHistory(historyRes.data);
      setReferralStats(referralsRes.data);
      setLoyaltyConfig(loyaltySettingsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('customerToken');
        localStorage.removeItem('customerData');
        navigate('/');
      } else {
        toast.error('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      const response = await api.put('/customers/me/profile', editData);
      setCustomer(response.data);
      setEditing(false);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleAddAddress = async () => {
    if (!newAddress.address.trim()) {
      toast.error('Please enter an address');
      return;
    }
    
    try {
      await api.post('/customers/me/addresses', newAddress);
      toast.success('Address added!');
      setShowAddAddress(false);
      setNewAddress({ label: 'Home', address: '', is_default: false });
      fetchData();
    } catch (error) {
      toast.error('Failed to add address');
    }
  };

  const handleDeleteAddress = async (addressId) => {
    try {
      await api.delete(`/customers/me/addresses/${addressId}`);
      toast.success('Address removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete address');
    }
  };

  const handleGenerateReferral = async () => {
    try {
      const response = await api.post('/loyalty/generate-referral');
      setLoyaltyBalance(prev => ({ ...prev, referral_code: response.data.referral_code }));
      toast.success('Referral code generated!');
      fetchData();
    } catch (error) {
      toast.error('Failed to generate referral code');
    }
  };

  const handleApplyReferral = async () => {
    if (!referralCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }
    
    setApplyingReferral(true);
    try {
      const response = await api.post('/loyalty/apply-referral', { referral_code: referralCode });
      toast.success(`${response.data.message} You earned ${response.data.bonus_earned} points!`);
      setShowReferral(false);
      setReferralCode('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply referral');
    } finally {
      setApplyingReferral(false);
    }
  };

  const copyReferralCode = () => {
    if (loyaltyBalance?.referral_code) {
      navigator.clipboard.writeText(loyaltyBalance.referral_code);
      setCopiedCode(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const shareReferral = () => {
    const settings = loyaltyBalance?.settings || {};
    const bonusPoints = settings.referral_bonus_referee || 50;
    const rupeesPerPoint = settings.rupees_per_point || 0.25;
    const bonusValue = (bonusPoints * rupeesPerPoint).toFixed(2);
    
    if (navigator.share && loyaltyBalance?.referral_code) {
      navigator.share({
        title: `Fish on Fire - Get ₹${bonusValue} off!`,
        text: `Use my referral code ${loyaltyBalance.referral_code} to get ${bonusPoints} bonus points (worth ₹${bonusValue}) on Fish on Fire!`,
        url: window.location.origin
      });
    } else {
      copyReferralCode();
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTierIcon = (tierName) => {
    switch(tierName) {
      case 'Gold': return <Crown className="w-5 h-5" />;
      case 'Silver': return <Award className="w-5 h-5" />;
      default: return <Star className="w-5 h-5" />;
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Ignore logout errors
    }
    localStorage.removeItem('customerToken');
    localStorage.removeItem('customerData');
    toast.success('Logged out successfully');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF4500] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" className="text-white p-2">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>
                My Profile
              </h1>
            </div>
            <Button 
              variant="outline"
              onClick={handleLogout}
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <Tabs defaultValue={loyaltyConfig?.loyalty_enabled !== false ? "rewards" : "profile"} className="space-y-6">
          <TabsList className="bg-[#121212] border border-white/10">
            {loyaltyConfig?.loyalty_enabled !== false && (
              <>
                <TabsTrigger value="rewards">Rewards</TabsTrigger>
                <TabsTrigger value="referrals">Referrals</TabsTrigger>
              </>
            )}
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="addresses">Addresses</TabsTrigger>
          </TabsList>

          {/* Rewards Tab - Only shown if loyalty is enabled */}
          {loyaltyConfig?.loyalty_enabled !== false && (
          <TabsContent value="rewards">
            <div className="space-y-6">
              {/* Tier Card */}
              {loyaltyBalance?.tier && (
                <Card 
                  className="border-2"
                  style={{ 
                    backgroundColor: `${loyaltyBalance.tier.color}15`,
                    borderColor: loyaltyBalance.tier.color 
                  }}
                >
                  <CardContent className="py-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-14 h-14 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${loyaltyBalance.tier.color}30` }}
                        >
                          <span style={{ color: loyaltyBalance.tier.color }}>
                            {getTierIcon(loyaltyBalance.tier.name)}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-xl font-bold" style={{ color: loyaltyBalance.tier.color }}>
                            {loyaltyBalance.tier.name} Member
                          </h3>
                          <p className="text-gray-400 text-sm">
                            {loyaltyBalance.tier.multiplier}x points on every order
                          </p>
                        </div>
                      </div>
                      {loyaltyBalance.next_tier && (
                        <div className="text-right">
                          <p className="text-gray-400 text-sm">Next tier: {loyaltyBalance.next_tier.name}</p>
                          <p className="text-white text-sm font-semibold">
                            {loyaltyBalance.next_tier.points_needed} points to go
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Points Balance Card */}
              <Card className="bg-gradient-to-br from-[#FF4500]/20 to-[#FF5722]/10 border-[#FF4500]/30">
                <CardContent className="py-8">
                  <div className="text-center">
                    <Gift className="w-12 h-12 mx-auto text-[#FF4500] mb-4" />
                    <h2 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Bebas Neue' }}>
                      {loyaltyBalance?.balance || 0} Points
                    </h2>
                    <p className="text-[#00E5FF] text-lg">
                      Worth {formatCurrency(loyaltyBalance?.points_value || 0)}
                    </p>
                    {loyaltyBalance?.expiring_soon > 0 && (
                      <p className="mt-2 text-yellow-400 text-sm">
                        ⚠️ {loyaltyBalance.expiring_soon} points expiring in 30 days
                      </p>
                    )}
                    {loyaltyBalance?.can_redeem ? (
                      <Badge className="mt-4 bg-green-500/20 text-green-400">
                        Ready to redeem at checkout!
                      </Badge>
                    ) : (
                      <p className="mt-4 text-gray-400 text-sm">
                        Earn {(loyaltyBalance?.min_redeem || 100) - (loyaltyBalance?.balance || 0)} more points to redeem
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* How it works */}
              <Card className="bg-[#121212] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white" style={{ fontFamily: 'Bebas Neue' }}>
                    Tier Benefits
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 rounded-lg" style={{ backgroundColor: '#CD7F3215' }}>
                      <Star className="w-8 h-8 mx-auto mb-2" style={{ color: '#CD7F32' }} />
                      <p className="text-white font-semibold">Bronze</p>
                      <p className="text-gray-400 text-sm">1x points</p>
                      <p className="text-gray-500 text-xs mt-1">0 - 499 points</p>
                    </div>
                    <div className="text-center p-4 rounded-lg" style={{ backgroundColor: '#C0C0C015' }}>
                      <Award className="w-8 h-8 mx-auto mb-2" style={{ color: '#C0C0C0' }} />
                      <p className="text-white font-semibold">Silver</p>
                      <p className="text-gray-400 text-sm">{loyaltyBalance?.settings?.silver_multiplier || 1.25}x points</p>
                      <p className="text-gray-500 text-xs mt-1">{loyaltyBalance?.settings?.silver_min_points || 500} - {(loyaltyBalance?.settings?.gold_min_points || 2000) - 1} points</p>
                    </div>
                    <div className="text-center p-4 rounded-lg" style={{ backgroundColor: '#FFD70015' }}>
                      <Crown className="w-8 h-8 mx-auto mb-2" style={{ color: '#FFD700' }} />
                      <p className="text-white font-semibold">Gold</p>
                      <p className="text-gray-400 text-sm">{loyaltyBalance?.settings?.gold_multiplier || 1.5}x points</p>
                      <p className="text-gray-500 text-xs mt-1">{loyaltyBalance?.settings?.gold_min_points || 2000}+ points</p>
                    </div>
                  </div>
                  <p className="text-center text-gray-500 text-xs mt-4">
                    Points expire {loyaltyBalance?.settings?.points_expiry_days || 180} days after earning
                  </p>
                </CardContent>
              </Card>

              {/* Points History */}
              <Card className="bg-[#121212] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Bebas Neue' }}>
                    <History className="w-5 h-5" />
                    Points History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loyaltyHistory.length === 0 ? (
                    <p className="text-gray-400 text-center py-8">No points history yet</p>
                  ) : (
                    <div className="space-y-3 max-h-80 overflow-y-auto">
                      {loyaltyHistory.map((transaction) => (
                        <div 
                          key={transaction.id} 
                          className="flex items-center justify-between py-3 border-b border-white/5 last:border-0"
                        >
                          <div>
                            <p className="text-white">{transaction.description}</p>
                            <p className="text-gray-500 text-xs">{formatDate(transaction.created_at)}</p>
                          </div>
                          <span className={`font-bold ${
                            transaction.points > 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {transaction.points > 0 ? '+' : ''}{transaction.points}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          )}

          {/* Referrals Tab - Only shown if loyalty is enabled */}
          {loyaltyConfig?.loyalty_enabled !== false && (
          <TabsContent value="referrals">
            <div className="space-y-6">
              {/* Your Referral Code */}
              <Card className="bg-gradient-to-br from-purple-500/20 to-pink-500/10 border-purple-500/30">
                <CardContent className="py-8">
                  <div className="text-center">
                    <Share2 className="w-12 h-12 mx-auto text-purple-400 mb-4" />
                    <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Bebas Neue' }}>
                      Refer & Earn
                    </h2>
                    <p className="text-gray-400 mb-6">
                      Share your code & get {formatCurrency(25)} when friends order!
                    </p>
                    
                    {loyaltyBalance?.referral_code ? (
                      <div className="space-y-4">
                        <div className="bg-[#121212] rounded-lg p-4 flex items-center justify-center gap-4 max-w-xs mx-auto">
                          <span className="font-mono text-2xl font-bold text-white">
                            {loyaltyBalance.referral_code}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={copyReferralCode}
                            className="text-purple-400 hover:text-purple-300"
                          >
                            {copiedCode ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                          </Button>
                        </div>
                        <Button
                          onClick={shareReferral}
                          className="bg-purple-500 hover:bg-purple-600 text-white"
                        >
                          <Share2 className="w-4 h-4 mr-2" />
                          Share with Friends
                        </Button>
                      </div>
                    ) : (
                      <Button
                        onClick={handleGenerateReferral}
                        className="bg-purple-500 hover:bg-purple-600 text-white"
                      >
                        Generate My Code
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Referral Stats */}
              {referralStats && (
                <Card className="bg-[#121212] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white" style={{ fontFamily: 'Bebas Neue' }}>
                      Your Referral Stats
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-4 bg-white/5 rounded-lg">
                        <p className="text-3xl font-bold text-white">{referralStats.total_referrals}</p>
                        <p className="text-gray-400 text-sm">Friends Referred</p>
                      </div>
                      <div className="text-center p-4 bg-white/5 rounded-lg">
                        <p className="text-3xl font-bold text-green-400">{referralStats.total_bonus_earned}</p>
                        <p className="text-gray-400 text-sm">Bonus Points Earned</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Apply Referral Code */}
              <Card className="bg-[#121212] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white" style={{ fontFamily: 'Bebas Neue' }}>
                    Have a Referral Code?
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400 text-sm mb-4">
                    Apply a friend's code to get {loyaltyBalance?.settings?.referral_bonus_referee || 50} bonus points (worth {formatCurrency((loyaltyBalance?.settings?.referral_bonus_referee || 50) * (loyaltyBalance?.settings?.rupees_per_point || 0.25))})!
                  </p>
                  <div className="flex gap-2">
                    <Input
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                      placeholder="Enter referral code"
                      className="bg-white/5 border-white/10 text-white font-mono"
                    />
                    <Button
                      onClick={handleApplyReferral}
                      disabled={applyingReferral || !referralCode}
                      className="bg-[#FF4500] hover:bg-[#FF5722] text-white"
                    >
                      {applyingReferral ? '...' : 'Apply'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          )}

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="bg-[#121212] border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white" style={{ fontFamily: 'Bebas Neue' }}>
                  Personal Information
                </CardTitle>
                {!editing ? (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setEditing(true)}
                    className="border-white/20 text-white"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setEditing(false)}
                      className="border-white/20 text-white"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm"
                      onClick={handleSaveProfile}
                      className="bg-[#00C853] hover:bg-[#00E676] text-white"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-full bg-[#FF4500]/20 flex items-center justify-center">
                    <User className="w-8 h-8 text-[#FF4500]" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">{customer?.name || 'Guest'}</p>
                    <p className="text-gray-400">{customer?.phone}</p>
                  </div>
                </div>

                {editing ? (
                  <div className="space-y-4">
                    <div>
                      <Label className="text-white mb-2 block">Name</Label>
                      <Input
                        value={editData.name}
                        onChange={(e) => setEditData({...editData, name: e.target.value})}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <Label className="text-white mb-2 block">Email</Label>
                      <Input
                        type="email"
                        value={editData.email}
                        onChange={(e) => setEditData({...editData, email: e.target.value})}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-white/5">
                      <span className="text-gray-400">Phone</span>
                      <span className="text-white">{customer?.phone}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-white/5">
                      <span className="text-gray-400">Name</span>
                      <span className="text-white">{customer?.name || 'Not set'}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-400">Email</span>
                      <span className="text-white">{customer?.email || 'Not set'}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Addresses Tab */}
          <TabsContent value="addresses">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>
                  Saved Addresses
                </h2>
                <Button
                  onClick={() => setShowAddAddress(true)}
                  className="bg-[#FF4500] hover:bg-[#FF5722] text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Address
                </Button>
              </div>

              {(!customer?.saved_addresses || customer.saved_addresses.length === 0) ? (
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="py-12 text-center">
                    <MapPin className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                    <p className="text-gray-400">No saved addresses</p>
                    <p className="text-gray-500 text-sm">Add addresses for faster checkout</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {customer.saved_addresses.map((addr) => (
                    <Card key={addr.id} className="bg-[#121212] border-white/10">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <MapPin className="w-5 h-5 text-[#00E5FF] mt-1" />
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-white font-semibold">{addr.label}</span>
                                {addr.is_default && (
                                  <Badge className="bg-[#FF4500]/20 text-[#FF4500] text-xs">Default</Badge>
                                )}
                              </div>
                              <p className="text-gray-400 text-sm">{addr.address}</p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAddress(addr.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Address Dialog */}
      <Dialog open={showAddAddress} onOpenChange={setShowAddAddress}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>
              Add New Address
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white mb-2 block">Label</Label>
              <Select value={newAddress.label} onValueChange={(v) => setNewAddress({...newAddress, label: v})}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="Home">Home</SelectItem>
                  <SelectItem value="Work">Work</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white mb-2 block">Full Address</Label>
              <Input
                value={newAddress.address}
                onChange={(e) => setNewAddress({...newAddress, address: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Enter your full address"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_default"
                checked={newAddress.is_default}
                onChange={(e) => setNewAddress({...newAddress, is_default: e.target.checked})}
                className="w-4 h-4"
              />
              <Label htmlFor="is_default" className="text-white">Set as default address</Label>
            </div>
            <Button
              onClick={handleAddAddress}
              className="w-full bg-[#FF4500] hover:bg-[#FF5722] text-white py-6"
            >
              Add Address
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerProfile;
